import { useCallback, useEffect, useState } from 'react';
import { fmtPT } from '../lib/ptTime';
import axios from 'axios';
import { useTicketApi } from '../lib/ticketApi';
import { Radio } from 'lucide-react';
import { useLiveClock } from '../lib/gameClock';
import GamePlayersPanel from './GamePlayersPanel';


const GameClock = ({ g }) => <>{useLiveClock(g.label, g.clock_sec, g.clock_at, g.clock_running)}</>;

const status = (g) => {
  if (g.state === 'in') return g.label ? <GameClock g={g} /> : `Q${g.period} · ${g.clock}`;
  if (g.state === 'post') return 'Final';
  return fmtPT(g.date);
};

const Side = ({ t, ball, lead, onTap, testId }) => (
  <div className="flex items-center gap-2 rounded hover:bg-white/5" role="button" data-testid={testId}
    onClick={(e) => { e.stopPropagation(); onTap(); }}>
    {t.logo && <img src={t.logo} alt="" className="w-6 h-6 object-contain" />}
    <span className={`flex-1 text-sm font-bold ${lead ? 'text-white' : 'text-zinc-400'}`}>{t.abbr}{ball && <span className="ml-1 text-amber-400" title="Has the ball">●</span>}</span>
    <span className={`font-mono text-lg font-bold ${lead ? 'text-white' : 'text-zinc-400'}`}>{t.score ?? '–'}</span>
  </div>
);

// Same colours as ticket legs, from your team bets on this game: tied yellow, trailing orange, leading blue.
const TILE = {
  yellow: 'border-2 border-yellow-400 bg-yellow-400/5',
  orange: 'border-2 border-orange-500 bg-orange-500/15',
  blue: 'border-2 border-sky-400 bg-sky-500/15',
  green: 'border-2 border-emerald-400 bg-emerald-500/15',
  red: 'border-2 border-red-500 bg-red-500/20',
};

const GameTile = ({ g, onOpen }) => {
  const a = Number(g.away.score || 0), h = Number(g.home.score || 0);
  const live = g.state === 'in';
  return (
    <div role="button" onClick={() => onOpen(g, null)} className={`min-w-[150px] flex-1 cursor-pointer rounded-xl border p-2.5 transition-transform active:scale-[0.98] ${TILE[g.tone] || (live ? 'border-red-500/50 bg-red-950/20' : 'border-zinc-800 bg-zinc-900/70')}`} data-tone={g.tone || undefined} data-testid={`scoreboard-game-${g.event_id}`}>
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="rounded bg-zinc-800 px-1.5 font-bold text-zinc-300">{g.league.toUpperCase()}</span>
        <span className={live ? 'text-red-300 font-bold' : 'text-zinc-500'} data-testid={`scoreboard-status-${g.event_id}`}>{live && '● '}{status(g)}</span>
      </div>
      <Side t={g.away} ball={g.possession === 'away'} lead={g.state === 'pre' || a >= h} onTap={() => onOpen(g, 'away')} testId={`scoreboard-away-${g.event_id}`} />
      <Side t={g.home} ball={g.possession === 'home'} lead={g.state === 'pre' || h >= a} onTap={() => onOpen(g, 'home')} testId={`scoreboard-home-${g.event_id}`} />
      {g.possession && (
        <p className={`mt-1 text-[10px] ${g.red_zone ? 'text-red-300 font-bold' : 'text-amber-300'}`} data-testid={`scoreboard-poss-${g.event_id}`}>
          {[`${g[g.possession].abbr} ball`, `${g.down_distance || ''}${g.spot ? ` at ${g.spot}` : ''}`.trim(), g.red_zone && 'RED ZONE'].filter(Boolean).join(' · ')}
        </p>
      )}
      <p className="mt-0.5 text-[10px] text-zinc-500">{g.legs} leg{g.legs === 1 ? '' : 's'} on your tickets · tap to see players</p>
    </div>
  );
};

// Live scores for every NFL / NBA / college football game that's on an open ticket. Refreshes every 20 s while a game is live.
export default function ActiveScoreboard({ reloadKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const api = useTicketApi();
  const load = useCallback(() => axios.get(`${api.base}/active-scoreboard`, { headers: api.headers }).then((r) => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => {
    const t = setInterval(load, data?.live ? 20000 : 60000);
    return () => clearInterval(t);
  }, [load, data?.live]);
  if (!data?.games?.length) return null;
  return (
    <section className="space-y-2" data-testid="active-scoreboard">
      <div className="flex items-center gap-2">
        <Radio className={`w-4 h-4 ${data.live ? 'text-red-400 animate-pulse' : 'text-zinc-500'}`} />
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-300">Active Scoreboards</p>
        <span className="text-[10px] text-zinc-500">{data.live ? `${data.live} live · updates every 20 s` : 'games on your open tickets'}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-3 lg:grid-cols-4 sm:overflow-visible">
        {data.games.map((g) => <GameTile key={`${g.league}-${g.event_id}`} g={g} onOpen={(game, side) => setOpen({ id: game.event_id, side })} />)}
      </div>
      {open && data.games.find((x) => x.event_id === open.id) && (
        <GamePlayersPanel g={data.games.find((x) => x.event_id === open.id)} side={open.side} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}
